import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'context/StoreContext';

/** Channels the storefront words itself; anything else uses the server's label. */
const KNOWN = ['qris', 'virtual_account', 'ewallet', 'card', 'retail', 'paylater'];

const upperFirst = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

function joinList(items, language) {
  if (items.length < 2) return items.join('');
  try {
    return new Intl.ListFormat(language === 'en' ? 'en' : 'id', { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/**
 * How the shop describes what it accepts, from the store profile payment
 * groups: "QRIS dan transfer bank (Virtual Account)".
 *
 *   summary    the sentence part, or null when the shop does not say
 *   qrisOnly   true when QRIS is the only group (the shopper sees a QR)
 *   known      whether the profile lists any group at all
 *
 * Pure function so it can be tested without React.
 */
export function describePayment(groups, t, language) {
  const list = Array.isArray(groups) ? groups : [];
  const phrases = list
    .map((g) => (KNOWN.includes(g.channel) ? t(`payment.channels.${g.channel}`) : g.label))
    .filter(Boolean);
  return {
    summary: phrases.length ? joinList(phrases, language) : null,
    qrisOnly: list.length > 0 && list.every((g) => g.channel === 'qris'),
    known: list.length > 0,
  };
}

/** Same, from the current shop profile and language; never throws on a profile without groups. */
export default function usePaymentCopy() {
  const { t, i18n } = useTranslation();
  const { store } = useStore();
  const groups = store && store.payment && store.payment.groups;
  const language = i18n.language;
  return useMemo(() => {
    const d = describePayment(groups, t, language);
    return { ...d, title: d.summary ? upperFirst(d.summary) : null, groups: groups || [] };
  }, [groups, t, language]);
}
